<template>
  <div class="container-fluid py-4">
    <div class="row my-5">
      <div class="col-md-2">
        <h4 class="mb-0">Hotel Reservations</h4>
      </div>
      <div class="col-md-8">
        <div class="d-flex">
          <input type="date" v-model="filters.start_date" class="form-control mr-2" />
          <input type="date" v-model="filters.end_date" class="form-control mr-2" />
          <select v-model="filters.payment_status" class="form-control mr-2">
            <option value="">All Payments</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partial</option>
          </select>
          <a class="btn btn-outline-primary" @click="fetchReservations">Filter</a>
        </div>
      </div>
      <div class="col-md-2">
        <a class="btn btn-primary btn-block" href="/make-reservations">Make a Reservation</a>
      </div>
    </div>

    <table class="table table-hover align-middle py-5">
      <thead class="thead-light">
        <tr>
          <th>#</th>
          <th>Guest</th>
          <th>Room</th>
          <th>Status</th>
          <th>Payment</th>
          <th>Check-in</th>
          <th>Check-out</th>
          <th class="text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="reservation in reservations" :key="reservation.id">
          <td @click.prevent="viewReservation(reservation.id)">{{ reservation.reservation_number }}</td>
          <td @click.prevent="viewReservation(reservation.id)">{{ reservation.customer?.full_name }}</td>
          <td @click.prevent="viewReservation(reservation.id)">{{ reservation.product.product }}</td>
          <td><span class="badge" :class="statusClass(reservation.status)">{{ reservation.status }}</span></td>
          <td>{{ reservation.total_amount }}</td>
          <td>{{ reservation.check_in }}</td>
          <td>{{ reservation.check_out }}</td>
          <td class="text-right"></td>
        </tr>
        <tr v-if="reservations.length === 0">
          <td colspan="8" class="text-center text-muted">No reservations found</td>
        </tr>
      </tbody>
    </table>

    <!-- Reservation Modal -->
    <div class="modal fade" id="reservationModal" tabindex="-1" role="dialog" aria-labelledby="reservationModalLabel" aria-hidden="true" data-backdrop="static" data-keyboard="false">
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
        <div class="modal-content border-0 shadow-lg rounded-lg">
          <div class="modal-header bg-light border-0">
            <button type="button" class="close text-dark" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body" v-if="reservation">
            <div v-if="loadingReservationDetails" class="d-flex flex-column justify-content-center align-items-center">
              <div class="spinner-border mb-3" role="status"></div>
              <p>Loading ...</p>
            </div>

            <div v-else>
              <div class="row">
                <div class="col-6">
                  <h5 v-if="reservation.reservation" class="modal-title font-weight-bold text-dark">Reservation #{{ reservation.reservation.reservation_number }}</h5>
                </div>
                <div class="col-6 text-right">
                  <div class="dropdown">
                    <button class="btn btn-link p-0" type="button" id="dropdownMenuButton" data-toggle="dropdown">
                      Actions <i class="fas fa-caret-down"></i>
                    </button>
                    <div class="dropdown-menu dropdown-menu-right shadow-sm">
                      <a class="dropdown-item" @click.prevent="checkInReservation(reservation.reservation.id)">Check-in</a>
                      <a class="dropdown-item" @click.prevent="checkOutReservation(reservation.reservation.id)">Check-out</a>
                      <a class="dropdown-item" @click.prevent="cancelReservation(reservation.reservation.id)">Cancel</a>

                      <div class="dropdown-divider"></div>
                      <!-- <a class="dropdown-item" @click.prevent="setItemsReservation(reservation.reservation.id)">Add Items</a> -->
                      <a class="dropdown-item" @click.prevent="openAttachOrderModal(reservation.reservation.id)">Attach Order</a>
                      <!-- <a class="dropdown-item" @click.prevent="openDetachOrderModal(reservation.reservation.id)">Detach Order</a> -->
                      <a class="dropdown-item" @click.prevent="addPaymentReservation(reservation.reservation.id)">Add Payment</a>
                      <a class="dropdown-item" @click.prevent="extendReservation(reservation.reservation.id)">Extend</a>
                      <a class="dropdown-item" @click.prevent="transferReservation(reservation.reservation.id)">Transfer</a>
                    </div>
                  </div>
                </div>
              </div>

              <h5 class="py-3 border-top border-dark" v-if="reservation.room">Room/House: {{ reservation.room.product }}</h5>
              <hr />

              <!-- Summary -->
              <div class="mb-4">
                <h6 class="section-title">Summary</h6>
                <div class="row">
                  <div class="col-md-6 mb-2">
                    <strong>Status:</strong>
                    <span class="badge" :class="statusClass(reservation.status)">{{ reservation.status }}</span>
                  </div>
                  <div class="col-md-6 mb-2">
                    <strong>Guests:</strong>
                    {{ reservation.guests?.adults || 0 }} Adults, {{ reservation.guests?.children || 0 }} Children
                  </div>
                  <div class="col-md-6 mb-2" v-if="reservation.dates?.length">
                    <strong>Check-in:</strong> {{ formatDate(reservation.dates[0]) }}
                  </div>
                  <div class="col-md-6 mb-2" v-if="reservation.dates?.length > 1">
                    <strong>Check-out:</strong> {{ formatDate(reservation.dates[1]) }}
                  </div>
                </div>
              </div>

              <!-- Segments -->
              <div v-if="reservation.segments?.length" class="mb-4">
                <h6 class="section-title">Stay Details</h6>
                <div class="table-responsive">
                  <table class="table table-sm table-hover modern-table">
                    <thead>
                      <tr>
                        <th>Start</th>
                        <th>End</th>
                        <th>Adults</th>
                        <th>Children</th>
                        <th class="text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(segment, index) in reservation.segments" :key="index">
                        <td>{{ formatDate(segment.start_date) }}</td>
                        <td>{{ formatDate(segment.end_date) }}</td>
                        <td>{{ segment.adults }}</td>
                        <td>{{ segment.children }}</td>
                        <td class="text-right">{{ convertCurrencyToLocal(segment.amount) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>


              <!-- orders -->
              <div v-if="reservation.orders?.length" class="mb-4">
                <h6 class="section-title">Orders</h6>
                <div class="table-responsive">
                  <table class="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th></th>

                      </tr>
                    </thead>
                    <tbody>

                      <tr  v-for="(order, index) in reservation.orders" :key="index">
                         <td @click="openOrderDetails(order.id)">{{ order.order }}</td>
                        <td @click="openOrderDetails(order.id)" class="text-right">{{ convertCurrencyToLocal(order.total_amount) }}</td>
                        <td @click="openOrderDetails(order.id)" class="text-right">
                            <span v-if="order.status === 1" class="badge badge-success">
                                Cleared
                            </span>
                            <span v-if="order.partial === 1 && order.status===0" class="badge badge-warning">
                                Partial
                            </span>
                            <span v-if="order.partial === 0 && order.status===0" class="badge badge-danger">
                                Unpaid
                            </span>
                        </td>
                        <td @click="openOrderDetails(order.id)" class="text-right">{{ formatDate(order.created_at,'HH:mm DD-MM-YYYY') }}</td>
                        <td>
                            <a @click="detachOrderFromReservation(reservation.reservation.id,order.id)" class="btn-sm btn btn-danger">Detach Order</a>
                        </td>


                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <!-- Payment Summary -->
              <div>
                <h6 class="section-title">Payment</h6>
                <div class="d-flex justify-content-between mb-2">
                  <span>Total Amount:</span>
                  <span class="font-weight-bold">{{ convertCurrencyToLocal(reservation.total_amount) }}</span>
                </div>
                <div class="d-flex justify-content-between mb-2">
                  <span>Amount Paid:</span>
                  <span class="text-success">{{ convertCurrencyToLocal(reservation.amount_paid) }}</span>
                </div>
                <div class="d-flex justify-content-between">
                  <span>Balance:</span>
                  <span class="text-danger font-weight-bold">{{ convertCurrencyToLocal(reservation.balance) }}</span>
                </div>
              </div>

              <div class="modal-footer border-0">
                <button type="button" class="btn btn-light" data-dismiss="modal">Close</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Attach Order Modal -->
    <div class="modal fade" id="attachOrderModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content rounded-lg">
          <div class="modal-header">
            <h5 class="modal-title">Attach Order</h5>
            <button class="close" data-dismiss="modal">&times;</button>
          </div>
          <div class="modal-body">
            <!-- <select v-model="selectedOrder" class="form-control">
              <option value="">Select Order</option>
              <option v-for="order in orders" :key="order.id" :value="order.id">
                {{ order.order_number }} | KSH {{ convertCurrencyToLocal(order.total) }}
              </option>
            </select> -->
            <p>You can search the order, just type the order number</p>
            <form @submit.prevent="searchOrderByOrderNumber">
                <input type="text" class="form-control" v-model="searchOrderValue" placeholder="Order Number" />
            <div class="form-group py-3">
                    <button type="submit" class="btn btn-primary">Search Order</button>
            </div>
            </form>


            <div v-if="orders">
                <div class="table-responsive">
 <table  class="table table-sm table-striped table-hover">
                    <thead>
                        <td>#</td>
                        <td>Order</td>
                        <td>Amount</td>
                        <td>Created At</td>
                    </thead>
                    <tbody>
                        <tr @click="attachOrderToReservation(order.id,reservation.reservation.id)" :key="order.id" v-for="order in orders">
                            <td></td>
                            <td>{{ order.order  }}</td>
                            <td>{{ convertCurrencyToLocal(order.total_amount) }}</td>
                            <td>{{ formatDate(order.created_at,"HH:mm DD-MM-YYYY") }}</td>
                        </tr>
                    </tbody>
                </table>
                </div>

            </div>

          </div>

        </div>
      </div>
    </div>

    <!-- Detach Order Modal -->
    <!-- <div class="modal fade" id="detachOrderModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content rounded-lg">
          <div class="modal-header">
            <h5 class="modal-title">Detach Order</h5>
            <button class="close" data-dismiss="modal">&times;</button>
          </div>
          <div class="modal-body">
            <select v-model="selectedOrder" class="form-control">
              <option value="">Select Order to Detach</option>
              <option v-for="order in reservation.orders" :key="order.id" :value="order.id">
                {{ order.order_number }} | KSH {{ convertCurrencyToLocal(order.total) }}
              </option>
            </select>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" data-dismiss="modal">Close</button>
            <button class="btn btn-danger" @click="detachOrderFromReservation">Detach</button>
          </div>
        </div>
      </div>
    </div> -->
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  data() {
    return {
      reservations: [],
      reservation: {},
      filters: {
        start_date: "",
        end_date: "",
        payment_status: "",
      },
      loading: false,
      loadingReservationDetails: false,
      orders: [],
      selectedOrder: "",
      searchOrderValue:''
    };
  },
  created() {
    this.fetchReservations();
  },
  methods: {
    async fetchReservations() {
      this.loading = true;
      try {
        const res = await axios.get("/api/reservations", { params: this.filters });
        this.reservations = res.data;
      } finally {
        this.loading = false;
      }
    },

    async viewReservation(id) {
      this.loadingReservationDetails = true;
      $('#reservationModal').modal('show');
      const res = await axios.get(`/api/reservations/statement/${id}`);
      this.reservation = res.data;
      this.loadingReservationDetails = false;
    },

    async checkInReservation(id) {
      await axios.post(`/api/reservations/check-in/${id}`);
      this.fetchReservations();
    },
    async checkOutReservation(id) {
      await axios.post(`/api/reservations/check-out/${id}`);
      this.fetchReservations();
    },
    async cancelReservation(id) {
      await axios.post(`/api/reservations/cancel/${id}`);
      this.fetchReservations();
    },

    async setItemsReservation(id) {
      await axios.post(`/api/reservations/items/${id}`);
    },
    async addPaymentReservation(id) {
      await axios.post(`/api/reservations/payment/${id}`);
      this.fetchReservations();
    },
    async extendReservation(id) {
      await axios.post(`/api/reservations/extend/${id}`);
      this.fetchReservations();
    },
    async transferReservation(id) {
      await axios.post(`/api/reservations/transfer/${id}`);
      this.fetchReservations();
    },

    async openAttachOrderModal(id) {
      this.selectedOrder = "";

      this.reservation.reservation.id = id;
      $('#attachOrderModal').modal('show');
    },


    async searchOrderByOrderNumber() {
     this.orders = (await axios.get("/api/allorders",{params:{
        'search': this.searchOrderValue,
      }})).data.orders.data;
    },
    async attachOrderToReservation(order_id,reservation_id) {
      if (!order_id) return;
      await axios.post(`/api/reservations/attach-order/${reservation_id}`, { final_order_id: order_id });
      $('#attachOrderModal').modal('hide');
      this.viewReservation(reservation_id);
    },

    async openDetachOrderModal(id) {
      this.selectedOrder = "";
      this.reservation.id = id;
      $('#detachOrderModal').modal('show');
    },

    async detachOrderFromReservation(reservation_id, order_id) {

      await axios.post(`/api/reservations/detach-order/${reservation_id}`, { final_order_id: order_id });
      this.$toasts.push({
        type:'success',
        message:"Order detached"
      })
      this.viewReservation(reservation_id);
    },

    formatDate(date,format=null) {

        if(!format)
        return moment(date).format('DD-MM-YYYY')
        else
        return moment(date).format(format)

    },
    openOrderDetails(order_id){
        window.location.href=`/orders?order-number=${order_id}`
    },

    statusClass(status) {
      return {
        confirmed: "badge-success",
        pending: "badge-warning",
        cancelled: "badge-danger",
        checked_in: "badge-info",
        checked_out: "badge-dark",
      }[status] || "badge-secondary";
    },
  },
};
</script>

<style scoped>
.table td,
.table th {
  vertical-align: middle;
}
.modal-content {
  border-radius: 1rem;
}
.section-title {
  font-size: 0.85rem;
  text-transform: uppercase;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.75rem;
  letter-spacing: 0.5px;
}
.modern-table thead th {
  background: #f9fafb;
  font-size: 0.8rem;
  text-transform: uppercase;
  font-weight: 600;
  color: #6c757d;
  border-bottom: 1px solid #dee2e6;
}
.badge-success {
  background-color: #28a745;
}
.badge-warning {
  background-color: #ffc107;
}
.badge-danger {
  background-color: #dc3545;
}
.badge-info {
  background-color: #17a2b8;
}
.badge-dark {
  background-color: #343a40;
}
</style>
